import React from 'react'
import { format as formatDate } from 'date-fns'
import { Box, Button, Link, Stack, Typography } from '@mui/material'
import { InfoOutlined, WarningAmberRounded } from '@mui/icons-material'

// Story 213, reopening 2 (PO 2026-10-22, C14.3, Main.dc.html) — what Camera control shows to an ADMIN or SUPERADMIN whose own
// screen is covered: the failure, with the choice of turning the vigilance off or waiting, and then the result.

// The line under the title: since when the vision service is down and how many sessions are covered, each only when the
// summary brings it (S285), then the risk of turning it off.
export const failureDetailText = (summary, t_) => {
  const since = summary.service && summary.service.unavailableSince
  const parts = []
  if (since && !Number.isNaN(new Date(since).getTime())) {
    parts.push(t_('Vigilance unavailable since {{time}}', { time: formatDate(new Date(since), 'HH:mm') }))
  }
  if (typeof summary.coveredSessions === 'number') {
    parts.push(t_('{{count}} users covered', { count: summary.coveredSessions }))
  }
  const risk = t_('If you turn it off, nothing is watched until you turn it on again.')
  return parts.length > 0 ? `${parts.join(' · ')}. ${risk}` : risk
}

// "← Back to where you were": the screen the user came from (the overlay again while it stays covered, S286).
export const BackLink = ({ onBack, t_, arrow = false }) => (
  <Link
    href='#'
    underline='none'
    onClick={(event) => {
      event.preventDefault()
      onBack()
    }}
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, alignSelf: 'flex-start' }}
  >
    {arrow ? '← ' : ''}{t_('Back to where you were')}
  </Link>
)

export const FailureNotice = ({ summary, onWait, onTurnOff, t_ }) => (
  <Box data-testid='camera-control-failure' role='alert' sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, px: 2.5, py: 2, borderRadius: 3, bgcolor: '#3A2A27', border: '1px solid #F68D76' }}>
    <WarningAmberRounded sx={{ color: '#F68D76', fontSize: 28 }} />
    <Stack spacing={0.5} sx={{ flex: '1 1 360px', minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{t_('Camera vigilance is failing: you can turn it off or wait')}</Typography>
      <Typography variant='body2' sx={{ color: '#E6E6E6' }}>{failureDetailText(summary, t_)}</Typography>
    </Stack>
    <Stack direction='row' spacing={1.25}>
      <Button variant='outlined' color='inherit' onClick={onWait} sx={{ borderRadius: 999, minHeight: 44 }}>
        {/* Judge C14-7: the verb; the existing "Wait" is a noun («Espera»), so this is its i18next context "action". */}
        {t_('Wait', { context: 'action' })}
      </Button>
      <Button variant='contained' onClick={onTurnOff} sx={{ borderRadius: 999, minHeight: 44, bgcolor: '#F68D76', color: '#1F1F1F', '&:hover': { bgcolor: '#F4A493' } }}>
        {t_('Turn vigilance off')}
      </Button>
    </Stack>
  </Box>
)

// R-8 (S285, S287): after deciding, or when it uncovers by itself, the page stays and says what happened.
export const ResultNotice = ({ turnedOff, onBack, t_ }) => (
  <Box data-testid='camera-control-result' role='status' sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, px: 2.5, py: 2, borderRadius: 3, bgcolor: '#383838', border: '1px solid #6A6A6A' }}>
    <InfoOutlined sx={{ color: '#CFCFCF' }} />
    <Typography variant='body2' sx={{ flex: '1 1 360px', color: '#E6E6E6' }}>
      {turnedOff
        ? t_('Vigilance turned off: you can work now. Turn it on again when the fault is fixed.')
        : t_('Still on: covered users get back to work as soon as vigilance recovers.')}
    </Typography>
    <BackLink onBack={onBack} t_={t_} />
  </Box>
)
